// Self-documenting preset specs for game/risk-deciding admin settings.
// AUTO-GENERATED from workflow wf_8f7eed5e-58e (adversarially verified 2026-08-06):
// preset VALUES bracket the live config, and every consequence line was checked
// against the engine for factual/direction accuracy. Edit the workflow + regenerate
// rather than hand-editing copy. See memory settings_ui_self_documenting_presets.
import type { ExplainedSettingProps } from '@baaligames/shared-ui/admin/components/ExplainedSetting';

export type SettingSpec = {
  key: string; title: string; help: string;
  unitPrefix?: string; unitSuffix?: string;
  customMin: number; customMax: number; customStep: number;
  zeroLabel?: string;
  presets: { label: string; value: number; tagline: string; consequence: string; tone: 'recommended' | 'safe' | 'neutral' | 'risky' }[];
  customDescribe: string;
};

// Turn a data spec into ExplainedSetting props (functions can't live in the data,
// so formatValue + custom.describe are derived here). Rupee values (unitPrefix '₹')
// format with Indian grouping; a value <= 0 shows the spec's zeroLabel when set.
export function explainedPropsFromSpec(spec: SettingSpec, value: any, onChange: (v: any) => void): ExplainedSettingProps {
  const isZero = (v: any) => spec.zeroLabel != null && (v === '' || v == null || Number(v) <= 0);
  const fmtNum = (v: any) => (spec.unitPrefix === '₹' ? `₹${Number(v).toLocaleString('en-IN')}` : `${v}${spec.unitSuffix ? ' ' + spec.unitSuffix : ''}`);
  return {
    title: spec.title, help: spec.help, value, onChange,
    formatValue: (v: any) => (isZero(v) ? spec.zeroLabel! : fmtNum(v)),
    presets: spec.presets,
    custom: {
      kind: 'number', min: spec.customMin, max: spec.customMax, step: spec.customStep,
      unitPrefix: spec.unitPrefix, unitSuffix: spec.unitSuffix,
      placeholder: `e.g. ${spec.presets[1]?.value ?? ''}`,
      describe: (v: any) => (isZero(v)
        ? `${spec.zeroLabel}.`
        : spec.customDescribe.replace('{v}', spec.unitPrefix === '₹' ? Number(v).toLocaleString('en-IN') : String(v))),
    },
  };
}

export const EXCHANGE_SETTING_SECTIONS: { title: string; specs: SettingSpec[] }[] = [
  {
    "title": "Edge & fees",
    "specs": [
      {
        "key": "house_margin_pct",
        "title": "House Margin (Edge on Odds)",
        "help": "This is the built-in edge baked into every cricket price you show. It's how the house earns on each bet, win or lose — the odds are quietly shaved a little in your favour. Set it higher and you keep more per bet, but players see worse prices and slowly play less; set it lower and your odds look sharp, pulling in more bets on a thinner cut.",
        "unitSuffix": "%",
        "zeroLabel": "No edge",
        "customMin": 0,
        "customMax": 20,
        "customStep": 0.5,
        "presets": [
          {
            "label": "Sharp",
            "value": 2,
            "tagline": "Sharpest odds, thinnest cut",
            "consequence": "Your cricket odds look almost razor-sharp, which is great for pulling in bets and building volume — but the house only keeps a thin 2% on each one. Pick this to compete hard on price and win on turnover rather than margin.",
            "tone": "risky"
          },
          {
            "label": "Standard",
            "value": 5,
            "tagline": "The usual house edge",
            "consequence": "A 5% edge is the normal cut for a cricket exchange. Players still see fair, competitive odds, and the house earns a steady margin on every bet. A safe default for most operators.",
            "tone": "recommended"
          },
          {
            "label": "Heavy",
            "value": 8,
            "tagline": "Most profit per bet",
            "consequence": "The house takes a fat 8% on every cricket price, so you earn the most per bet — but sharp players will notice the worse odds and may bet less or drift elsewhere. Choose this when protecting margin matters more than volume.",
            "tone": "safe"
          }
        ],
        "customDescribe": "Every cricket bet is priced with a {v}% edge for the house — higher keeps more profit per bet and is safer for the house, lower shows players sharper odds and thins your edge."
      },
      {
        "key": "commission_pct",
        "title": "Exchange Commission (on Winnings)",
        "help": "When two players bet against each other in Exchange mode, the house takes a small slice of the winner's profit. This setting is that slice. Set it higher and you earn more on player-vs-player books, but your exchange looks less attractive; set it lower and it becomes a magnet for bettors, on a smaller cut each time.",
        "unitSuffix": "%",
        "zeroLabel": "No cut",
        "customMin": 0,
        "customMax": 30,
        "customStep": 0.5,
        "presets": [
          {
            "label": "Low",
            "value": 3,
            "tagline": "Cheapest exchange, most bettors",
            "consequence": "Winners give up just 3% of their profit, so your exchange is one of the cheapest around — a real draw for serious bettors. The house earns little per win and leans on heavy volume to make it pay.",
            "tone": "risky"
          },
          {
            "label": "Standard",
            "value": 10,
            "tagline": "The usual exchange cut",
            "consequence": "A 10% cut on winnings is the normal rate for a betting exchange. Bettors accept it as fair, and the house earns a healthy slice of every player-vs-player win. A solid default.",
            "tone": "recommended"
          },
          {
            "label": "High",
            "value": 15,
            "tagline": "Biggest house cut",
            "consequence": "The house keeps 15% of every winner's profit — the most revenue per win. But keen exchange bettors compare rates, and some will bet less or move to a cheaper book. Choose this when squeezing revenue matters more than growing the exchange.",
            "tone": "safe"
          }
        ],
        "customDescribe": "The house keeps {v}% of the winner's profit on every Exchange bet — higher earns the house more and is safer for revenue, lower makes the exchange more attractive and keeps less."
      }
    ]
  },
  {
    "title": "Exposure caps",
    "specs": [
      {
        "key": "rm_max_bet_liability",
        "title": "Max loss from a single bet",
        "help": "This is the most the house is allowed to lose on any ONE bet a player places. If a single bet could put the house on the hook for more than this, that bet is simply blocked before it's accepted. Think of it as your safety net against one giant bet doing real damage. A higher figure lets bigger single bets ride (more action, bigger possible loss); a lower figure turns big bets away sooner (safer, but high-rollers may feel boxed in). Set it to 0 to remove the cap completely — not recommended.",
        "unitPrefix": "₹",
        "zeroLabel": "Unlimited",
        "customMin": 0,
        "customMax": 5000000,
        "customStep": 10000,
        "customDescribe": "Any single bet that could cost the house more than ₹{v} is blocked before it is accepted — a lower number is safer for the house, a higher one lets bigger bets through.",
        "presets": [
          {
            "label": "Cautious",
            "value": 50000,
            "tagline": "Small single-bet risk",
            "consequence": "Any bet that could cost the house more than ₹50,000 is turned away. Very little risk from any one bet, but your biggest players will hit this ceiling often and may feel boxed in.",
            "tone": "safe"
          },
          {
            "label": "Balanced",
            "value": 100000,
            "tagline": "Room for serious players",
            "consequence": "No single bet can cost the house more than ₹1 lakh. Plenty of room for serious players while stopping any one bet from doing real damage — a safe, sensible default.",
            "tone": "recommended"
          },
          {
            "label": "High-roller",
            "value": 250000,
            "tagline": "Big single bets welcome",
            "consequence": "The house can be on the hook for up to ₹2.5 lakh from a single bet before it's blocked. Great for pulling in high-rollers and bigger revenue, but one bad result on a single bet hits harder.",
            "tone": "risky"
          }
        ]
      },
      {
        "key": "rm_runner_exposure_ceiling",
        "title": "Auto-suspend a market when one team gets too risky",
        "help": "This watches each team (or selection) in a market and works out the worst the house could lose if that team wins, across every bet taken. The moment any one team's worst-case loss reaches this figure, the WHOLE market is auto-suspended — all betting on it stops — so no more money can pile onto that hot side. A higher figure lets a lot build up on one side before the market locks (riskier); a lower figure locks earlier and keeps your exposure small (safer, though players may find a market suspended mid-match). Set it to 0 to never auto-suspend on a single team — not recommended.",
        "unitPrefix": "₹",
        "zeroLabel": "No auto-suspend",
        "customMin": 0,
        "customMax": 5000000,
        "customStep": 50000,
        "customDescribe": "The whole market is automatically suspended once the house's worst-case loss on any one team reaches ₹{v} — a lower number pulls the brake sooner and is safer for the house.",
        "presets": [
          {
            "label": "Suspend early",
            "value": 250000,
            "tagline": "Keeps per-team risk small",
            "consequence": "The market locks the moment the house's worst-case loss on any one team hits ₹2.5 lakh. Your exposure on any single side stays small, but players may find a popular market suspended earlier in the game.",
            "tone": "safe"
          },
          {
            "label": "Balanced",
            "value": 500000,
            "tagline": "Comfortable per-team room",
            "consequence": "The market auto-suspends once the house's worst-case loss on any one team reaches ₹5 lakh. Enough room for a busy match while capping how much any one side can hurt you — a balanced choice.",
            "tone": "recommended"
          },
          {
            "label": "Let it build",
            "value": 1500000,
            "tagline": "Lots can ride on one team",
            "consequence": "A single side can build up to ₹15 lakh of worst-case house loss before the market auto-suspends. Lots of action stays open on one side, but the house is carrying heavy risk if that side comes in.",
            "tone": "risky"
          }
        ]
      },
      {
        "key": "rm_market_exposure_ceiling",
        "title": "Hard cap on a whole market's worst-case loss",
        "help": "A market is a full betting question — like 'who wins the match', with all its teams counted together. This is the outer limit on the most the house could lose on that market, whichever result lands. Once a market reaches this figure, the house refuses any new bet that would push its worst-case loss higher — the market stops taking on more risk (it stays open for bets that don't add to the danger). It's the big-picture ceiling that sits above the per-team brake. A higher figure lets more risk ride on one market before bets are turned away (riskier); a lower figure turns risky bets away sooner (safer, though keen players on a hot market get refused earlier). Whatever you set, the house never carries more than ₹50 lakh of worst-case loss on one market — a fixed safety rail. Set it to 0 to fall back to that ₹50 lakh rail.",
        "unitPrefix": "₹",
        "zeroLabel": "₹50L rail",
        "customMin": 0,
        "customMax": 5000000,
        "customStep": 100000,
        "customDescribe": "The house refuses any new bet that would push a market's worst-case loss past ₹{v} — a lower number turns risky bets away sooner and is safer for the house.",
        "presets": [
          {
            "label": "Tight cap",
            "value": 500000,
            "tagline": "Turns risky bets away sooner",
            "consequence": "The house turns away any bet that would take a market past ₹5 lakh of worst-case loss. Very controlled exposure, though keen players on a hot market get refused earlier.",
            "tone": "safe"
          },
          {
            "label": "Balanced",
            "value": 1000000,
            "tagline": "Comfortable per-market room",
            "consequence": "The house won't let a market's worst-case loss pass ₹10 lakh — bets beyond that are refused. A solid big-picture ceiling that lets a market run without letting it run away — a sensible default.",
            "tone": "recommended"
          },
          {
            "label": "Loose cap",
            "value": 3000000,
            "tagline": "Big money per market",
            "consequence": "A market can carry up to ₹30 lakh of worst-case house loss before further bets are refused. Maximum action, but a lot is riding on one market going your way.",
            "tone": "risky"
          }
        ]
      },
      {
        "key": "rm_fancy_exposure_ceiling",
        "title": "Hard cap on a session (fancy) bet's worst-case loss",
        "help": "Session or 'fancy' bets are the side bets on things like runs in an over, a batsman's score, or the next few overs. They move fast and can swing hard, so they get their own tighter cap. Once the house's worst-case loss on one session reaches this figure, the house refuses any new bet on that session that would push it higher. A higher figure lets more ride on a fast-moving session (riskier — these can turn in a few balls); a lower figure turns bets away early (safer, and usually the smart choice for fancy). Whatever you set, no session can carry more than the ₹50 lakh house rail. Set it to 0 to fall back to that ₹50 lakh rail — far too loose for fancy, not recommended.",
        "unitPrefix": "₹",
        "zeroLabel": "₹50L rail",
        "customMin": 0,
        "customMax": 5000000,
        "customStep": 50000,
        "customDescribe": "The house refuses any new session bet that would push its worst-case loss past ₹{v} — a lower cap is safer, because fancy bets swing fast.",
        "presets": [
          {
            "label": "Tight cap",
            "value": 150000,
            "tagline": "Cuts volatile sessions early",
            "consequence": "The house turns away any session bet beyond ₹1.5 lakh of worst-case loss. Very little risk on these volatile side-bets, though keen fancy players get refused sooner.",
            "tone": "safe"
          },
          {
            "label": "Balanced",
            "value": 300000,
            "tagline": "Sensible for session bets",
            "consequence": "The house won't let a session's worst-case loss pass ₹3 lakh — bets beyond that are refused. A tight, sensible cap that suits how fast fancy bets move — a good default.",
            "tone": "recommended"
          },
          {
            "label": "Loose cap",
            "value": 750000,
            "tagline": "More ride on a fast bet",
            "consequence": "One session can carry up to ₹7.5 lakh of worst-case house loss before further bets are refused. Keeps fast side-bets open longer, but these swing quickly and can turn against you in a few balls.",
            "tone": "risky"
          }
        ]
      },
      {
        "key": "default_max_match_exposure",
        "title": "Player match exposure cap",
        "help": "Sets the most any single player can have riding on one match at the same time — their ceiling on that one game, win or lose. Set it to 0 to remove the ceiling entirely (unlimited). Higher lets your biggest bettors go large, which means more volume but bigger swings for the house; lower keeps any one player's damage on a single match small.",
        "unitPrefix": "₹",
        "zeroLabel": "Unlimited",
        "customMin": 0,
        "customMax": 10000000,
        "customStep": 50000,
        "customDescribe": "Each player can have at most ₹{v} at risk on a single match at once — set it to 0 for no limit (unlimited), and lower keeps the house safer.",
        "presets": [
          {
            "label": "High rollers welcome",
            "value": 500000,
            "tagline": "Let big players bet large",
            "consequence": "Any one player can have up to ₹5 lakh live on a single match. Great for pulling volume from whales, but one match can swing hard against the house if a big bettor gets lucky.",
            "tone": "risky"
          },
          {
            "label": "Balanced",
            "value": 200000,
            "tagline": "Sensible cap for most books",
            "consequence": "No single player can have more than ₹2 lakh at stake on one match. Comfortable room for serious bettors while capping how much one match can hurt you. A good default.",
            "tone": "recommended"
          },
          {
            "label": "Tight leash",
            "value": 100000,
            "tagline": "Keep every player small",
            "consequence": "Caps any one player at ₹1 lakh on a single match. Your risk on any game stays low, but big bettors will feel boxed in and may bet less.",
            "tone": "safe"
          }
        ]
      },
      {
        "key": "default_max_aggregate_exposure",
        "title": "Player total exposure cap (all matches)",
        "help": "Sets the most any single player can have riding across every live match at once — their whole book with you at that moment, added up. This master ceiling sits on top of the per-match cap. Set it to 0 to remove it (unlimited). Higher lets active players spread big money across many games; lower keeps any one player's total risk to the house contained.",
        "unitPrefix": "₹",
        "zeroLabel": "Unlimited",
        "customMin": 0,
        "customMax": 50000000,
        "customStep": 100000,
        "customDescribe": "A player can hold at most ₹{v} at risk across all live matches combined — set it to 0 for no limit (unlimited), and lower is safer for the house.",
        "presets": [
          {
            "label": "Loose ceiling",
            "value": 5000000,
            "tagline": "Room to bet across many games",
            "consequence": "One player can have up to ₹50 lakh working across all live matches combined. Maximises action from your most active bettors, but a single hot night for one of them hits the house hard.",
            "tone": "risky"
          },
          {
            "label": "Balanced",
            "value": 2000000,
            "tagline": "Sensible total for most books",
            "consequence": "Caps each player at ₹20 lakh across everything live at once. Plenty of room for busy bettors while keeping the house's worst case from one person in check. A solid default.",
            "tone": "recommended"
          },
          {
            "label": "Contained",
            "value": 1000000,
            "tagline": "Keep each player's book small",
            "consequence": "No player can hold more than ₹10 lakh total across all live matches. Your exposure to any single person stays tight, though heavy bettors will hit the wall sooner.",
            "tone": "safe"
          }
        ]
      }
    ]
  },
  {
    "title": "Liquidity & credit",
    "specs": [
      {
        "key": "broker_credit_limit",
        "title": "Broker credit limit",
        "help": "This is how much a broker (agent) is allowed to owe the house before the brakes come on. The moment their unpaid balance crosses this line, every player under them is blocked from placing new bets until they settle up. Set it to 0 to switch the limit off, so brokers can owe any amount. Lower means you chase payment sooner and carry less bad-debt risk; higher (or off) extends more trust and keeps their players betting longer.",
        "unitPrefix": "₹",
        "zeroLabel": "Off",
        "customMin": 0,
        "customMax": 10000000,
        "customStep": 25000,
        "customDescribe": "A broker's whole downline is blocked from new bets once the broker owes more than ₹{v} — set it to 0 to turn the limit off, and lower protects the house from bad debt.",
        "presets": [
          {
            "label": "Generous credit",
            "value": 250000,
            "tagline": "Extend more trust before blocking",
            "consequence": "A broker can run up to ₹2.5 lakh owing before their players are stopped. Keeps action flowing and keeps brokers happy, but you're carrying more risk if one never pays.",
            "tone": "risky"
          },
          {
            "label": "Balanced",
            "value": 100000,
            "tagline": "Sensible leash for most brokers",
            "consequence": "Brokers are cut off once they owe ₹1 lakh. Enough rope for normal settlement cycles while keeping your bad-debt exposure sensible. A good default.",
            "tone": "recommended"
          },
          {
            "label": "Collect early",
            "value": 50000,
            "tagline": "Short leash, chase payment fast",
            "consequence": "Players are blocked as soon as their broker owes ₹50,000. You collect sooner and rarely carry much unpaid, but brokers get halted more often and may grumble.",
            "tone": "safe"
          }
        ]
      },
      {
        "key": "p2p_bot_liquidity",
        "title": "Exchange house backstop",
        "help": "In Exchange mode, players bet against each other — but when nobody is on the other side of a bet, the house can step in and fill it so the player still gets matched. This sets the biggest single bet the house will fill that way. Set it to 0 for pure player-versus-player, where the house never steps in and players only ever match each other. Higher means the house will fill even very large single bets on its own, so the market always feels deep and liquid — but everything the house fills is a position the house now carries. Lower means the house only tops up smaller bets and leaves the rest waiting for a real player.",
        "unitPrefix": "₹",
        "zeroLabel": "Pure P2P (no backstop)",
        "customMin": 0,
        "customMax": 1000000000,
        "customStep": 1000000,
        "customDescribe": "The house will step in and fill a single Exchange bet up to ₹{v} when no player is on the other side — set it to 0 for pure player-versus-player with no house backstop, and lower leaves more to real players and keeps the house off the hook.",
        "presets": [
          {
            "label": "Always matched",
            "value": 250000000,
            "tagline": "House fills almost everything",
            "consequence": "The house will fill even a very large single bet on its own — up to ₹25 crore — so players are matched instantly whatever the size. The market always looks deep and liquid, but the house ends up carrying whatever players don't match among themselves.",
            "tone": "risky"
          },
          {
            "label": "Balanced",
            "value": 100000000,
            "tagline": "Deep liquidity, sensible ceiling",
            "consequence": "The house fills essentially any realistic bet by itself, up to ₹10 crore on a single one. The market stays fully liquid while capping how large a one-off position the house will take on. A strong default.",
            "tone": "recommended"
          },
          {
            "label": "Light backstop",
            "value": 25000000,
            "tagline": "Cap the biggest single fill",
            "consequence": "The house still fills every normal bet, but won't take on more than ₹2.5 crore on a single one in one go — so it won't swallow one abnormally large order whole. Everyday play stays liquid; for a truly player-driven book with no house backstop, set it to 0.",
            "tone": "safe"
          }
        ]
      }
    ]
  },
  {
    "title": "Live feed timing",
    "specs": [
      {
        "key": "odds_max_staleness_seconds",
        "title": "Reject bets on stale odds",
        "help": "Your live odds come from a data feed that can fall behind the real match. This refuses bets whenever the feed is older than the number of seconds you pick, so a player can't bet on a price the game has already moved past.",
        "unitSuffix": "sec",
        "zeroLabel": "Off",
        "customMin": 0,
        "customMax": 300,
        "customStep": 5,
        "customDescribe": "Bets are refused whenever the odds feed is more than {v} seconds behind live play — lower is safer for the house, and 0 turns the check off so prices of any age are accepted.",
        "presets": [
          {
            "label": "Relaxed",
            "value": 90,
            "tagline": "Accept older prices — up to 90 seconds behind live play.",
            "consequence": "Bets go through even when the feed is a minute and a half behind the real match. Betting almost never gets blocked, so players stay happy — but a sharp player can bet on a price you haven't updated yet, which favours them and can cost the house. Set 0 to turn this guard off completely.",
            "tone": "risky"
          },
          {
            "label": "Standard",
            "value": 45,
            "tagline": "Refuse bets once the feed is more than 45 seconds behind.",
            "consequence": "Bets are accepted on normal, reasonably fresh prices but blocked the moment the feed lags past 45 seconds. A good balance for most operators — it stops obvious stale-price sniping while keeping betting smooth. This is the usual choice.",
            "tone": "recommended"
          },
          {
            "label": "Strict",
            "value": 20,
            "tagline": "Only fresh prices — reject anything older than 20 seconds.",
            "consequence": "If the feed lags by more than 20 seconds, bets are refused until fresh prices arrive. This is the tightest guard against players sniping a price the market has already moved past — safest for the house, though players may see a 'try again' during feed hiccups.",
            "tone": "safe"
          }
        ]
      },
      {
        "key": "ball_suspend_seconds",
        "title": "Pause betting after each ball",
        "help": "Right after each ball is bowled, someone sitting at the ground knows the result before your feed does. This automatically freezes betting for a few seconds after every delivery so your prices can catch up before players can bet again.",
        "unitSuffix": "sec",
        "zeroLabel": "Off",
        "customMin": 0,
        "customMax": 60,
        "customStep": 1,
        "customDescribe": "After every delivery, betting is automatically frozen for {v} seconds before reopening — higher is safer for the house against ground-side sniping, and 0 turns the pause off.",
        "presets": [
          {
            "label": "Off",
            "value": 0,
            "tagline": "No pause — betting stays open right through every ball.",
            "consequence": "Betting never stops, even in the seconds right after a delivery. Smoothest experience for players, but someone physically at the ground sees the ball before your feed does and can bet on the outcome before you've priced it in. Riskiest for the house.",
            "tone": "risky"
          },
          {
            "label": "Standard",
            "value": 6,
            "tagline": "Freeze betting for 6 seconds after each ball.",
            "consequence": "The moment a ball is bowled, betting locks for 6 seconds and then reopens once your feed has caught up. This closes the small window where a person at the stadium could bet ahead of your prices, with only a brief pause between balls. A solid default.",
            "tone": "recommended"
          },
          {
            "label": "Strong",
            "value": 12,
            "tagline": "Freeze betting for 12 seconds after each ball.",
            "consequence": "Betting stays locked for a full 12 seconds after every delivery — plenty of time for your feed to reflect what happened before players can bet again. The strongest protection against ground-side (court-siding) cheating, at the cost of a more stop-start feel between balls.",
            "tone": "safe"
          }
        ]
      },
      {
        "key": "bet_delay_seconds",
        "title": "Hold and re-check each in-play bet",
        "help": "Holds every in-play bet for a moment and re-checks it against the very latest price before accepting it. Catches fast players trying to sneak a bet in a split-second ahead of a price change.",
        "unitSuffix": "sec",
        "zeroLabel": "Off",
        "customMin": 0,
        "customMax": 10,
        "customStep": 1,
        "customDescribe": "Each in-play bet is held for {v} seconds and re-priced against the latest odds before it is accepted — higher is safer for the house but feels laggier, 0 turns the delay off, and the max is 10.",
        "presets": [
          {
            "label": "Off",
            "value": 0,
            "tagline": "Accept in-play bets instantly, with no re-check.",
            "consequence": "Every live bet is taken the instant it's placed. Snappiest experience for players, but a fast player can fire a bet in the split second before your price updates and lock in an edge. Riskiest for the house.",
            "tone": "risky"
          },
          {
            "label": "Standard",
            "value": 3,
            "tagline": "Hold each bet 3 seconds, then re-price before accepting.",
            "consequence": "Each live bet waits 3 seconds and is re-checked against the latest price before it's accepted — long enough to catch a price that just moved, without making live betting feel slow. The usual, well-balanced default.",
            "tone": "recommended"
          },
          {
            "label": "Cautious",
            "value": 5,
            "tagline": "Hold each bet 5 seconds, then re-price before accepting.",
            "consequence": "Every live bet is parked for 5 seconds and re-checked against fresh prices before it stands. Strong protection against fast odds-sniping, but the extra wait makes live betting feel laggy, which some players dislike.",
            "tone": "safe"
          }
        ]
      }
    ]
  }
];

export const PLATFORM_SETTING_SPECS: SettingSpec[] = [
  {
    "key": "maxConcurrentMatchesPerUser",
    "title": "Games one player can run at once",
    "help": "Sets how many games or matches a single player is allowed to have going at the very same time. At one, they finish (or leave) a game before they can open another; higher numbers let them line several up together. More games at once means more fun and more play per visit — but also more money a single player can have riding at the same moment.",
    "unitSuffix": "at once",
    "customMin": 1,
    "customMax": 100,
    "customStep": 1,
    "zeroLabel": "",
    "customDescribe": "A single player can have up to {v} games running at the same time before they must finish one to start another — lower keeps play to one-at-a-time and is easier on house risk.",
    "presets": [
      {
        "label": "One at a time",
        "value": 1,
        "tagline": "Finish one, then start the next",
        "consequence": "Each player can only have one game going at a time — they must finish or leave it before joining another. Simplest to watch, and the tightest control on how much a single player can have riding at any one moment. The standard default for most houses.",
        "tone": "recommended"
      },
      {
        "label": "A few",
        "value": 3,
        "tagline": "Up to three going together",
        "consequence": "A player can keep up to three games running side by side. A bit more fun and more play per visit, and a little more money in the air per player at any one time. A comfortable middle ground.",
        "tone": "neutral"
      },
      {
        "label": "Many",
        "value": 10,
        "tagline": "Lots at the same time",
        "consequence": "A player can juggle up to ten games at once. The most engagement and the most action per player — but also the most a single player can have at stake at the same moment, which is harder to keep an eye on. Pick this only if you want maximum play.",
        "tone": "risky"
      }
    ]
  }
];
